import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { downloadXlsx } from '../../lib/downloadXlsx'

type Status = 'legat' | 'legat_prin_sku' | 'lipsa_in_ea'

interface Rand {
  shopifyProductId: string
  variantId: string
  inventoryItemId: string | null
  title: string
  variantTitle: string | null
  handle: string
  url: string
  statusShopify: string | null
  skuShopify: string | null
  price: number
  available: boolean
  imageUrl: string | null
  status: Status
  eaProductId: number | null
  eaSku: string | null
  eaName: string | null
  eaBoostId: string | null
  eaInventoryId: string | null
  ex3Shopify: string
  ex4Shopify: string | null
  ex3Diferit: boolean
  ex4Diferit: boolean
}

interface Orfan {
  id: number
  sku: string
  name: string
  boostId: string | null
  inventoryId: string | null
}

interface Resp {
  fetchedAt: string
  sursa: 'admin' | 'public'
  randuri: Rand[]
  orfane: Orfan[]
  sumar: {
    total: number
    legat: number
    legatPrinSku: number
    lipsaInEa: number
    orfane: number
    deCorectat: number
  }
}

const STATUS: Record<Status, { label: string; cls: string; hint: string }> = {
  legat: {
    label: 'Legat',
    cls: 'bg-green-100 text-green-800',
    hint: 'Boost ID din ExpertAccounts == id-ul variantei (sau al produsului) de pe site.',
  },
  legat_prin_sku: {
    label: 'Fără Boost ID în EA',
    cls: 'bg-amber-100 text-amber-800',
    hint: 'Găsit doar după SKU. Completează ex3 / ex4 în ExpertAccounts cu valorile din coloanele „Shopify".',
  },
  lipsa_in_ea: {
    label: 'Lipsă în EA',
    cls: 'bg-red-100 text-red-800',
    hint: 'Niciun articol din catalog nu are acest Boost ID sau SKU. Verifică în ExpertAccounts.',
  },
}

type Filtru = Status | 'toate' | 'de_corectat'

const FILTRE: Array<{ key: Filtru; label: string }> = [
  { key: 'toate', label: 'Toate' },
  { key: 'de_corectat', label: 'De corectat în EA' },
  { key: 'legat', label: 'Legate' },
  { key: 'legat_prin_sku', label: 'Fără Boost ID în EA' },
  { key: 'lipsa_in_ea', label: 'Lipsă în EA' },
]

function deCorectat(r: Rand): boolean {
  return (
    r.eaProductId != null &&
    (r.ex3Diferit || r.ex4Diferit || !r.eaBoostId || (!r.eaInventoryId && !!r.ex4Shopify))
  )
}

export function ArticoleSitePage() {
  const [filtru, setFiltru] = useState<Filtru>('toate')
  const [cauta, setCauta] = useState('')
  const [exporting, setExporting] = useState(false)

  const query = useQuery<Resp>({
    queryKey: ['shopify-articole-site'],
    queryFn: async () => (await api.get<Resp>('/shopify/articole-site')).data,
    staleTime: 5 * 60_000,
  })

  const randuri = useMemo(() => {
    const toate = query.data?.randuri ?? []
    const q = cauta.trim().toLowerCase()
    return toate.filter((r) => {
      if (filtru === 'de_corectat' && !deCorectat(r)) return false
      if (filtru !== 'toate' && filtru !== 'de_corectat' && r.status !== filtru) return false
      if (!q) return true
      return [
        r.title,
        r.variantTitle,
        r.skuShopify,
        r.shopifyProductId,
        r.variantId,
        r.inventoryItemId,
        r.eaSku,
        r.eaName,
        r.eaBoostId,
        r.eaInventoryId,
      ]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q))
    })
  }, [query.data, filtru, cauta])

  const sumar = query.data?.sumar
  const orfane = query.data?.orfane ?? []

  const exporta = async () => {
    try {
      setExporting(true)
      await downloadXlsx('/shopify/exports/articole-site', 'articole-site.xlsx')
    } catch (e) {
      toast.error((e as Error).message || 'Export eșuat')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Articole site ↔ ExpertAccounts</h1>
          <p className="text-xs text-b4l-text-muted">
            Variantele de pe boost4life.ro cu codul din ExpertAccounts și valorile pe care trebuie
            să le aibă <span className="font-mono">ex3</span> (Boost ID = id variantă Shopify) și{' '}
            <span className="font-mono">ex4</span> (Inventory ID = inventory_item_id).
            {query.data && (
              <>
                {' '}
                Citit la {new Date(query.data.fetchedAt).toLocaleTimeString('ro-RO')} din{' '}
                {query.data.sursa === 'admin' ? 'Shopify Admin API' : 'catalogul public'}.
              </>
            )}
          </p>
          {query.data?.sursa === 'public' && (
            <p className="mt-1 text-xs text-amber-700">
              Fără token Shopify (Setări → Shopify) nu se poate citi Inventory ID și nu se văd
              produsele nepublicate.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            className="rounded border border-b4l-border bg-white px-3 py-1.5 text-sm hover:bg-b4l-bg disabled:opacity-50"
          >
            {query.isFetching ? 'Se reîncarcă...' : 'Reîncarcă'}
          </button>
          <button
            onClick={exporta}
            disabled={exporting || !query.data}
            className="rounded bg-b4l-blue px-3 py-1.5 text-sm text-white hover:bg-b4l-blue-dark disabled:opacity-50"
          >
            {exporting ? 'Se exportă…' : 'Exportă în Excel'}
          </button>
        </div>
      </div>

      {sumar && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
          <Kpi label="Variante pe site" value={sumar.total} onClick={() => setFiltru('toate')} />
          <Kpi
            label="De corectat în EA"
            value={sumar.deCorectat}
            warn={sumar.deCorectat > 0}
            onClick={() => setFiltru('de_corectat')}
          />
          <Kpi label="Legate" value={sumar.legat} onClick={() => setFiltru('legat')} />
          <Kpi
            label="Fără Boost ID în EA"
            value={sumar.legatPrinSku}
            warn={sumar.legatPrinSku > 0}
            onClick={() => setFiltru('legat_prin_sku')}
          />
          <Kpi
            label="Lipsă în EA"
            value={sumar.lipsaInEa}
            warn={sumar.lipsaInEa > 0}
            onClick={() => setFiltru('lipsa_in_ea')}
          />
          <Kpi label="Boost ID orfan în EA" value={sumar.orfane} warn={sumar.orfane > 0} />
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-b4l-text-dim">Filtru</label>
            <div className="mt-1 flex flex-wrap gap-1">
              {FILTRE.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFiltru(f.key)}
                  className={`rounded border px-2 py-1 text-xs ${
                    filtru === f.key
                      ? 'border-b4l-blue bg-b4l-blue-light/30 text-b4l-blue-dark'
                      : 'border-b4l-border bg-white hover:bg-b4l-bg'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="min-w-[240px] flex-1">
            <label className="block text-xs font-medium text-b4l-text-dim">Caută</label>
            <input
              value={cauta}
              onChange={(e) => setCauta(e.target.value)}
              placeholder="produs, SKU, cod EA, id Shopify..."
              className="mt-1 w-full rounded border border-b4l-border px-2 py-1 text-sm"
            />
          </div>
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs font-semibold text-b4l-text-dim">
                <th className="px-2 py-2"></th>
                <th className="px-2 py-2">Produs pe site</th>
                <th className="px-2 py-2">SKU site</th>
                <th className="px-2 py-2">ID produs Shopify</th>
                <th className="px-2 py-2 bg-b4l-blue-light/20">
                  ex3 · Boost ID
                  <div className="font-normal text-[10px]">din Shopify (id variantă)</div>
                </th>
                <th className="px-2 py-2 bg-b4l-blue-light/20">
                  ex4 · Inventory ID
                  <div className="font-normal text-[10px]">din Shopify (inventory_item_id)</div>
                </th>
                <th className="px-2 py-2">Cod EA</th>
                <th className="px-2 py-2">Denumire EA</th>
                <th className="px-2 py-2">ex3 în EA</th>
                <th className="px-2 py-2">ex4 în EA</th>
                <th className="px-2 py-2 text-right">Preț site</th>
                <th className="px-2 py-2">Stare</th>
              </tr>
            </thead>
            <tbody>
              {query.isLoading && (
                <tr>
                  <td colSpan={12} className="px-2 py-6 text-center text-b4l-text-muted">
                    Se citește catalogul Shopify...
                  </td>
                </tr>
              )}
              {query.isError && (
                <tr>
                  <td colSpan={12} className="px-2 py-6 text-center text-b4l-red-dark">
                    {(query.error as Error).message || 'Nu am putut citi Shopify'}
                  </td>
                </tr>
              )}
              {!query.isLoading && !query.isError && randuri.length === 0 && (
                <tr>
                  <td colSpan={12} className="px-2 py-6 text-center text-b4l-text-muted">
                    Nimic de afișat pentru filtrul curent
                  </td>
                </tr>
              )}
              {randuri.map((r) => {
                const st = STATUS[r.status]
                return (
                  <tr key={r.variantId} className="border-b border-b4l-border hover:bg-b4l-bg-soft">
                    <td className="px-2 py-1.5">
                      {r.imageUrl ? (
                        <img
                          src={r.imageUrl}
                          alt=""
                          className="h-9 w-9 rounded object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-9 w-9 rounded bg-b4l-bg-soft" />
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-medium text-b4l-blue-dark hover:underline"
                      >
                        {r.title}
                      </a>
                      {r.variantTitle && (
                        <div className="text-[10px] text-b4l-text-muted">{r.variantTitle}</div>
                      )}
                      {r.statusShopify && r.statusShopify !== 'active' && (
                        <div className="text-[10px] text-amber-700">Shopify: {r.statusShopify}</div>
                      )}
                      {!r.available && (
                        <div className="text-[10px] text-b4l-red-dark">indisponibil pe site</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs">{r.skuShopify ?? '-'}</td>
                    <td className="px-2 py-1.5 font-mono text-xs text-b4l-text-muted">
                      <Copiabil text={r.shopifyProductId} />
                    </td>
                    <td className="px-2 py-1.5 bg-b4l-blue-light/10 font-mono text-xs font-medium">
                      <Copiabil text={r.ex3Shopify} />
                    </td>
                    <td className="px-2 py-1.5 bg-b4l-blue-light/10 font-mono text-xs font-medium">
                      {r.ex4Shopify ? <Copiabil text={r.ex4Shopify} /> : '-'}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs">{r.eaSku ?? '-'}</td>
                    <td className="px-2 py-1.5 text-xs">{r.eaName ?? '-'}</td>
                    <td className="px-2 py-1.5 font-mono text-xs">
                      <ValoareEa
                        valoare={r.eaBoostId}
                        diferit={r.ex3Diferit}
                        lipsa={r.eaProductId != null && !r.eaBoostId}
                      />
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs">
                      <ValoareEa
                        valoare={r.eaInventoryId}
                        diferit={r.ex4Diferit}
                        lipsa={r.eaProductId != null && !r.eaInventoryId && !!r.ex4Shopify}
                      />
                    </td>
                    <td className="px-2 py-1.5 text-right text-xs">
                      {r.price.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-2 py-1.5">
                      <span
                        title={st.hint}
                        className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium ${st.cls}`}
                      >
                        {st.label}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] text-b4l-text-muted">
          {randuri.length} din {query.data?.randuri.length ?? 0} variante. Coloanele albastre sunt
          valorile de pus în ExpertAccounts; roșu = EA are altă valoare, galben = lipsește în EA.
          Click pe un id îl copiază.
        </p>
      </Card>

      {orfane.length > 0 && (
        <Card>
          <h2 className="text-sm font-semibold">
            Articole din ExpertAccounts cu Boost ID care nu există pe site ({orfane.length})
          </h2>
          <p className="mb-2 text-xs text-b4l-text-muted">
            Varianta a fost ștearsă din Shopify sau Boost ID-ul din EA e greșit.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-b4l-border text-left font-semibold text-b4l-text-dim">
                  <th className="px-2 py-1.5">Cod EA</th>
                  <th className="px-2 py-1.5">Denumire EA</th>
                  <th className="px-2 py-1.5">ex3 în EA</th>
                  <th className="px-2 py-1.5">ex4 în EA</th>
                </tr>
              </thead>
              <tbody>
                {orfane.map((o) => (
                  <tr key={o.id} className="border-b border-b4l-border">
                    <td className="px-2 py-1.5 font-mono">{o.sku}</td>
                    <td className="px-2 py-1.5">{o.name}</td>
                    <td className="px-2 py-1.5 font-mono">{o.boostId}</td>
                    <td className="px-2 py-1.5 font-mono">{o.inventoryId ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}

function ValoareEa({
  valoare,
  diferit,
  lipsa,
}: {
  valoare: string | null
  diferit: boolean
  lipsa: boolean
}) {
  if (!valoare) {
    return lipsa ? (
      <span className="rounded bg-amber-100 px-1 text-amber-800" title="Lipsește în EA">
        lipsă
      </span>
    ) : (
      <span>-</span>
    )
  }
  return (
    <span
      title={diferit ? 'EA are altă valoare decât Shopify' : undefined}
      className={diferit ? 'rounded bg-red-100 px-1 text-red-800 line-through' : ''}
    >
      {valoare}
    </span>
  )
}

function Copiabil({ text }: { text: string }) {
  return (
    <button
      type="button"
      title="Copiază"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => toast.success('Copiat: ' + text))
      }}
      className="hover:underline"
    >
      {text}
    </button>
  )
}

function Kpi({
  label,
  value,
  warn,
  onClick,
}: {
  label: string
  value: number
  warn?: boolean
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded border bg-white p-3 text-left ${
        warn ? 'border-amber-300' : 'border-b4l-border'
      } ${onClick ? 'hover:bg-b4l-bg-soft' : 'cursor-default'}`}
    >
      <div className="text-[10px] uppercase tracking-wider text-b4l-text-dim">{label}</div>
      <div className={`mt-1 text-lg font-semibold ${warn ? 'text-amber-700' : ''}`}>{value}</div>
    </button>
  )
}
